import type { RichContentBlock } from '@core/services/ai/ai-assistant.service';

/**
 * Runtime shape guard for AI rich-content blocks.
 *
 * `richContent` arrives from Firestore documents written by the backend
 * and used to be cast blindly (`as RichContentBlock[]`) into the block
 * renderers. A block of a known kind with a missing required field
 * (deploy-window skew between backend and frontend, hand-edited docs,
 * future schema drift) could then crash a renderer mid-message.
 *
 * The checks are deliberately SHALLOW: one entry per kind listing the
 * fields whose absence would make its renderer throw (`.map`/`.reduce`
 * on a missing array, etc.). Item-level fields all render through
 * null-tolerant interpolation, so validating them here would be noise.
 */
type FieldRequirement = readonly [field: string, type: 'array' | 'string' | 'number' | 'object'];

const BLOCK_SHAPE_REQUIREMENTS: Record<RichContentBlock['kind'], readonly FieldRequirement[]> = {
  'summary': [['bullets', 'array'], ['title', 'string']],
  'entity-match': [['title', 'string']],
  'entity-summary': [['fields', 'array'], ['title', 'string']],
  'choice': [['choices', 'array']],
  'form-fields': [['fields', 'array'], ['formId', 'string']],
  'kpi-grid': [['items', 'array']],
  'entity-list': [['items', 'array']],
  'data-table': [['columns', 'array'], ['rows', 'array']],
  'timeline': [['events', 'array']],
  'question': [['prompt', 'string']],
  'bar-chart': [['bars', 'array']],
  'donut-chart': [['slices', 'array']],
  'status-tracker': [['steps', 'array']],
  'alert': [['title', 'string']],
  'spec-list': [['specs', 'array']],
  'metric-card': [['label', 'string']],
  'stat-comparison': [['left', 'object'], ['right', 'object']],
  'entity-pick': [['options', 'array']],
  'quote': [['text', 'string']],
  'progress-bar': [['label', 'string'], ['percent', 'number']],
  'address-card': [['address', 'string']],
  'code-block': [['code', 'string']],
  'navigate-action': [['label', 'string'], ['route', 'string']],
  'navigate-list': [['items', 'array']],
};

function isRenderableBlock(candidate: unknown): candidate is RichContentBlock {
  if (candidate === null || typeof candidate !== 'object') return false;
  const kind = (candidate as { kind?: unknown }).kind;
  if (typeof kind !== 'string') return false;
  const requirements = BLOCK_SHAPE_REQUIREMENTS[kind as RichContentBlock['kind']];
  // Unknown kind: nothing can render it — dropping it (with a log at the
  // call site) beats the silent blank the dispatcher would produce.
  if (!requirements) return false;
  const record = candidate as Record<string, unknown>;
  for (const [field, type] of requirements) {
    const value = record[field];
    if (type === 'array' && !Array.isArray(value)) return false;
    if (type === 'string' && typeof value !== 'string') return false;
    if (type === 'number' && typeof value !== 'number') return false;
    if (type === 'object' && (value === null || typeof value !== 'object')) return false;
  }
  return true;
}

export interface SanitizedRichContent {
  blocks: RichContentBlock[] | undefined;
  /** Compact descriptions of dropped entries, for logging. */
  rejected: string[];
}

/**
 * Validate a raw Firestore `richContent` value into renderable blocks.
 * Invalid entries are dropped and described in `rejected` so the caller
 * can log them — a message with one bad widget still renders the rest.
 */
export function sanitizeRichContentBlocks(raw: unknown): SanitizedRichContent {
  if (!Array.isArray(raw)) {
    return { blocks: undefined, rejected: [] };
  }
  const blocks: RichContentBlock[] = [];
  const rejected: string[] = [];
  for (const candidate of raw) {
    if (isRenderableBlock(candidate)) {
      blocks.push({ ...candidate });
    } else {
      const kind =
        candidate !== null && typeof candidate === 'object' ?
          String((candidate as { kind?: unknown }).kind ?? '<no kind>') :
          typeof candidate;
      rejected.push(kind);
    }
  }
  return { blocks: blocks.length > 0 ? blocks : undefined, rejected };
}
